import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtINR } from "@/lib/format";
import { RefLabel } from "@/lib/lookups";
import { MASTERS } from "@/lib/masters";
import { PageHeader, ErrorState, LoadingRows, NoAccess, Badge, EmptyState } from "@/components/common/States";
import { EntityForm } from "./EntityForm";

const LIMIT = 20;

export const CellValue = ({ col, d }) => {
  const { branches } = useSession();
  const v = d[col.key];
  if (col.render) return <><div className="font-medium">{col.render(d)}</div>{col.sub && <div className="text-xs text-muted-foreground font-mono-g">{col.sub(d)}</div>}</>;
  if (col.branch) return branches.find((b) => b.id === v)?.code || "—";
  if (col.ref) return <RefLabel kind={col.ref} id={v} />;
  if (col.bool) return v ? <Badge tone="gold">Yes</Badge> : <span className="text-muted-foreground">—</span>;
  if (col.money) return fmtINR(v);
  if (col.options) return Array.isArray(v) ? (v.length ? v.map((x) => col.options.find((o) => o.value === x)?.label || x).join(", ") : "All") : (col.options.find((o) => o.value === v)?.label || v || "—");
  if (v === null || v === undefined || v === "") return <span className="text-muted-foreground">—</span>;
  return <span className={col.mono ? "font-mono-g text-xs" : ""}>{String(v).replace(/_/g, " ")}</span>;
};

const FilterBar = ({ cfg, q, setQ, status, setStatus, filters, setFilters }) => (
  <div className="flex flex-col lg:flex-row gap-3 p-4 border-b bg-secondary/40">
    <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9 bg-white" placeholder={`Search ${cfg.plural.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${cfg.entity}-search-input`} /></div>
    {cfg.filters.map((f) => (
      <Select key={f.param} value={filters[f.param] || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, [f.param]: v === "all" ? undefined : v }))}>
        <SelectTrigger className="lg:w-48 bg-white" data-testid={`${cfg.entity}-filter-${f.param}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">{f.label}</SelectItem>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    ))}
    <Select value={status} onValueChange={setStatus}>
      <SelectTrigger className="lg:w-36 bg-white" data-testid={`${cfg.entity}-status-filter`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
    </Select>
  </div>
);

export default function MasterList({ kind }) {
  const cfg = MASTERS[kind];
  const { can, companyId } = useSession();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [status, setStatus] = useState("active");
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ key: null, order: "asc" });
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("new")) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  useEffect(() => { const t = setTimeout(() => { setDq(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [status, filters]);

  const params = { q: dq || undefined, status, page, limit: LIMIT, sort: sort.key || undefined, order: sort.order, ...filters };
  const { data, loading, error, reload } = useFetch(`/${kind}`, params, can(`${cfg.perm}.view`) && !!companyId);
  if (!can(`${cfg.perm}.view`)) return <NoAccess perm={`${cfg.perm}.view`} />;
  if (!companyId) {
    return (
      <div data-testid={`${kind}-list-page`}>
        <PageHeader eyebrow="Master Data" title={cfg.plural} subtitle={cfg.subtitle} />
        <div className="g39-card"><EmptyState title="Select a company" body="Master data belongs to a company. Choose one from the selector in the top bar." testId="select-company-state" /></div>
      </div>
    );
  }
  const filtering = !!dq || Object.values(filters).some(Boolean);
  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const toggleSort = (key) => setSort((s) => ({ key, order: s.key === key && s.order === "asc" ? "desc" : "asc" }));

  return (
    <div data-testid={`${kind}-list-page`}>
      <PageHeader eyebrow="Master Data" title={cfg.plural} subtitle={cfg.subtitle}
        actions={can(`${cfg.perm}.manage`) && <Button onClick={() => setFormOpen(true)} data-testid={`add-${cfg.entity}-button`}><Plus className="h-4 w-4 mr-1.5" />New {cfg.label.toLowerCase()}</Button>} />
      <div className="g39-card overflow-hidden">
        <FilterBar cfg={cfg} q={q} setQ={setQ} status={status} setStatus={setStatus} filters={filters} setFilters={setFilters} />
        {loading && !data && <LoadingRows />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.items.length && <EmptyState title={filtering ? "No matching records" : `No ${status === "inactive" ? "inactive " : ""}${cfg.plural.toLowerCase()} yet`} body={filtering ? "Try a different search or clear the filters." : (can(`${cfg.perm}.manage`) && status !== "inactive" ? `Create the first ${cfg.label.toLowerCase()} to get started.` : undefined)} testId={`${kind}-empty-state`} />}
        {data?.items.length > 0 && (
          <div className="overflow-x-auto">
            <Table data-testid={`${kind}-table`}>
              <TableHeader><TableRow>{cfg.columns.map((c) => (
                <TableHead key={c.key}>{c.sort ? <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`}>{c.label}<ArrowUpDown className="h-3 w-3" /></button> : c.label}</TableHead>
              ))}<TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer" onClick={() => navigate(`${cfg.path}/${d.id}`)} data-testid={`${cfg.entity}-row-${d.id}`}>
                    {cfg.columns.map((c) => <TableCell key={c.key} className="text-sm"><CellValue col={c} d={d} /></TableCell>)}
                    <TableCell><Badge tone={d.status}>{d.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm" data-testid={`${kind}-pagination`}>
            <span className="text-muted-foreground">{data.total} records · page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid={`${kind}-prev-page`}><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${kind}-next-page`}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>
      <EntityForm cfg={cfg} open={formOpen} onOpenChange={setFormOpen} onSaved={(d) => navigate(`${cfg.path}/${d.id}`)} />
    </div>
  );
}
